import { ComparisonLineChart } from 'src/components/analytics/charts/ComparisonLineChart'
import { NoData } from 'src/components/analytics/common/NoData'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { useOrderData } from './useOrderData'
import { useWidgetConfig } from 'src/components/overview/charts/common/useWidgetConfig'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId, WidgetSections } from 'src/lib/contexts/AnalyticsContext/types'

const getTooltipTitle = (dataKey: 'order_completed_count' | 'total_gmv' | 'aov') => {
  switch (dataKey) {
    case 'order_completed_count':
      return 'Orders'
    case 'total_gmv':
      return 'Sales'
    case 'aov':
      return 'Average Order Value'
  }
}

const getHeadingTitle = (dataKey: 'order_completed_count' | 'total_gmv' | 'aov') => {
  switch (dataKey) {
    case 'order_completed_count':
      return 'Orders over time'
    case 'total_gmv':
      return 'Sales over time'
    case 'aov':
      return 'Average order value over time'
  }
}

export default function OrdersChart({
  dataKey,
  isCurrency = false
}: {
  dataKey: 'order_completed_count' | 'total_gmv' | 'aov'
  isCurrency?: boolean
}) {
  const { orderData, isLoading, isError, ref, selectedTimeScale } = useOrderData({ dataKey })
  const { actions: { setAlertDialogState } } = useAnalytics()

  const widgetIdForAlert =
    dataKey === 'order_completed_count'
      ? WidgetId.orders
      : dataKey === 'total_gmv'
        ? WidgetId.orders_sales
        : WidgetId.orders_average_order_value

  const { threshold_alerts_supported } = useWidgetConfig(WidgetSections.orders, widgetIdForAlert)

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (orderData) {
      return (
        <>
          <div className='flex items-center gap-2 mt-2 mb-1'>
            <div className='text-[18px] font-bold text-carbon mb-1'>
              {isCurrency
                ? orderData.total.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                    style: 'currency',
                    currency: 'INR'
                  })
                : orderData.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </div>
            <TrendIndicator value={orderData.percentageChange} formatter={value => `${Math.abs(value)}%`} />
          </div>
          <hr />
          {/* todo: add a tooltip to the title */}
          <p className='text-sm font-medium mt-2 mb-4'>{getHeadingTitle(dataKey)}</p>
          <div className='w-full h-[78%]'>
            <ComparisonLineChart
              data={orderData}
              isNoComparison={false}
              yScaleType={'linear'}
              selectedTimeScale={selectedTimeScale}
              tooltipTitle={getTooltipTitle(dataKey)}
              isCurrency={isCurrency}
              thresholdAlertConfig={{
                isAlertsSupported: Boolean(threshold_alerts_supported),
                onClickAlert: (rate: number) => setAlertDialogState(true, widgetIdForAlert, rate)
              }}
            />
          </div>
          <div className='rounded-lg flex-1'>
            <div className='flex gap-6 mb-4 px-4 text-[#949494] text-xs w-full justify-center items-center'>
              <div className='flex items-center gap-2'>
                <div className='w-8 h-1 bg-primary-dark rounded-full'></div>
                <span>{orderData.currentDateRangeLegend}</span>
              </div>
              {orderData.previousDateRangeLegend && (
                <div className='flex items-center gap-2'>
                  <div className='w-8 h-1 border-t-2 border-dashed border-[#949494]'></div>
                  <span>{orderData.previousDateRangeLegend}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
